import { AssetGroup } from '@play-co/timestep-core';
import * as Level from 'src/replicant/level';
import * as Tutorial from 'src/replicant/tutorial';
import * as Decoration from 'src/replicant/decoration';
import {
  type DecorationItem,
  type DecorationItemID,
  type DecorationRoomID,
  type DecorationSlot,
  type DecorationTierID,
} from 'src/replicant/decoration/decoration.ruleset';
import { SelectedItemState } from 'src/replicant/decoration/decoration.state';
import { assertNever } from 'src/replicant/utils';
import StateObserver, { user, now } from 'src/StateObserver';
import { getCatalog } from 'src/replicant/catalog/catalog.getters';
import { getDecorationItem } from 'src/replicant/decoration/decoration.getters';
import { DecorationListItem } from './DecorationSelectItemView';
import { DecorationCatalogItem } from 'src/replicant/catalog/catalog.ruleset';
import { State } from 'src/state';
import { getCurrentGachaEvent } from 'src/replicant/gacha/gacha.getters';

/**
 * Feature unlock conditions check
 */
export const isDecorationUnlocked = () => hasMetDecorationUnlockCriteria();

/**
 * Gacha unlock conditions check
 */
export const isDecorationGachaUnlocked = () => {
  return hasMetDecorationUnlockCriteria();
};

/**
 * Check if gacha event is available
 * @returns
 */
export const isDecorationGachaEventAvailable = () =>
  isDecorationGachaUnlocked() && !!getCurrentGachaEvent(user(), now());

/**
 * Check if decoration is unlocked
 */
function hasMetDecorationUnlockCriteria() {
  return (
    Level.ruleset.toDisplay(user().level.current) >=
    Decoration.ruleset.decorationUnlockLevel
  );
}

/**
 * Return true if decoration tutorial is in progress
 */
export function isDecorationTutorialInProgress(): boolean {
  return (
    isDecorationUnlocked() &&
    !Tutorial.getters.isTutorialStepComplete(user(), 'decoration')
  );
}

/**
 * Get permanent(saved) item selection for this slot
 */
export function getSelectedItemForSlot(
  room: DecorationRoomID,
  slot: DecorationSlot,
): (DecorationItem & { tierId?: DecorationTierID }) | null {
  return Decoration.getters.getDecorationItemBySlot(user(), room, slot);
}

/**
 * Get permanent(saved) item selection for current room
 *
 * @param selected - If set it will use selected items not from current user state
 */
export function getSelectedItems(
  room: DecorationRoomID,
  selected?: SelectedItemState[],
) {
  return Decoration.getters.getSelectedRoomDecorations(
    selected ?? Decoration.getters.getSelectedDecorationsState(user()),
    room,
  );
}

/**
 * Check if decoration purchased
 */
export function isDecorationPurchased(id: DecorationItemID): boolean {
  return Decoration.getters.isDecorationPurchased(user(), id);
}

/**
 * Get path to decoration item asset
 */
export function getDecorationItemPath({
  image,
}: DecorationItem): string | undefined {
  return getDecorationPath(image);
}

/**
 * Get path to decoration item asset
 */
export function getDecorationPath(
  image: string | undefined,
): string | undefined {
  if (!image) return undefined;

  return `assets/decoration/${image}`;
}

/**
 * Get decorations for slots
 */
export function getAvailableDecorationsForSlot(
  slot: DecorationSlot,
  room: DecorationRoomID,
): DecorationListItem[] {
  // Owned items.
  const catalogDecorationItems = getCatalog(user()).filter(
    (x) => x.type === 'decoration',
  ) as DecorationCatalogItem[];

  const ownedItems = catalogDecorationItems
    .map((x) => ({
      ...getDecorationItem(x.id),
      stars: x.tier,
      isNew: x.isNew,
      image: x.image,
      tierId: x.tierId,
    }))
    .filter((item) => {
      return item.slot === slot && item.room === room;
    })
    .reduce((acc, item) => {
      const existing = acc.find((x) => x.id === item.id);
      // If item is already in the list, but this one has more stars, replace it.
      if (existing) {
        if (item.stars > existing.stars!) {
          Object.assign(existing, item);
        }
        return acc;
      }
      acc.push(item);
      return acc;
    }, [] as DecorationListItem[]);

  // Get default assets
  const defaultItems: DecorationListItem[] = Decoration.getters
    .getAvailableDecorations(room)
    .filter((item) => {
      return (
        item.slot === slot &&
        item.room === room &&
        item.theme === '01_default_theme'
      );
    })
    .map((item) => ({
      ...item,
      // Default items are always 2 stars. Except for empty slot.
      stars: item.image ? 2 : undefined,
    }));

  // Merge owned and default items.
  return [...defaultItems, ...ownedItems];
}

/**
 * Some slots should not be animated on change, like walls and floor
 */
export function shouldSkipShowupAnimation(
  room: DecorationRoomID,
  slot: DecorationSlot,
) {
  return isWallSlot(room, slot) || isFloorSlot(room, slot);
}

/**
 * Check if this wall slot for this room
 */
export function isWallSlot(
  room: DecorationRoomID,
  slot: DecorationSlot,
): boolean {
  switch (room) {
    case 'living':
    case 'toilet':
      return slot === 1; // 1 - in living room is always wall
    default:
      assertNever(room);
  }
}

/**
 * Check if this floor slot for this room
 */
export function isFloorSlot(
  room: DecorationRoomID,
  slot: DecorationSlot,
): boolean {
  switch (room) {
    case 'living':
    case 'toilet':
      return slot === 2; // 1 - in living room is always floor
    default:
      assertNever(room);
  }
}

/**
 * Get default required assets urls
 */
export function getDefaultRequiredAssetsURLs(
  { user }: State,
  room: DecorationRoomID,
): Map<DecorationSlot | 0, string> {
  switch (room) {
    case 'living':
      return new Map([
        [0, 'assets/ui/decoration/background/living_room_base.png'],
        [1, 'assets/decoration/01_living_room/default/01_wallpaper.png'],
        [2, 'assets/decoration/01_living_room/default/02_floor.png'],
      ]);
    case 'toilet':
      return new Map([
        [0, 'assets/ui/decoration/background/toilet_room_base.png'],
        [1, 'assets/decoration/04_toilet_room/default/01_wallpaper.png'],
        [2, 'assets/decoration/04_toilet_room/default/02_floor.png'],
      ]);
    default:
      assertNever(room);
  }
}

/**
 * Return minimal required decoration assets group for selected room
 */
export function getRequiredDecorationAssets(
  room: DecorationRoomID,
  selected?: SelectedItemState[],
): AssetGroup {
  const items = getSelectedItems(room, selected);
  const defaultAssets = getDefaultRequiredAssetsURLs(
    StateObserver.getState(),
    room,
  );
  const assets: Set<string> = new Set();

  // Default assets
  defaultAssets.forEach((image) => {
    assets.add(image);
  });

  // Override default
  for (const [slot, item] of items) {
    if (shouldSkipShowupAnimation(room, slot)) {
      const image = item.image && getDecorationPath(item.image);
      if (image) assets.add(image);
    }
  }

  return new AssetGroup(
    AssetGroup.constructURLs(Array.from(assets)),
    undefined,
    AssetGroup.PRIORITY_MEDIUM,
  );
}
